import { Link } from "react-router-dom";
import { Card } from "@shared/components/ui/card";
import { format } from "date-fns";
import {
  CalendarIcon,
  CheckCircle2,
  Clock,
  User2,
  MapPin,
  Phone,
  Store,
  Package,
  DollarSign,
  Eye,
  Receipt,
  Info,
  AlertCircle,
  XCircle,
} from "lucide-react";
import { Separator } from "@shared/components/ui/separator";
import { Badge } from "@shared/components/ui/badge";
import { Button } from "@shared/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/components/ui/tooltip";
import InvoiceActions from "./InvoiceActions";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { useTranslation } from "react-i18next";

// Status icons mapping
const statusIcons = {
  pending: <Clock className="size-4 text-yellow-500" />,
  paid: <CheckCircle2 className="size-4 text-green-500" />,
  overdue: <AlertCircle className="size-4 text-red-500" />,
  cancelled: <XCircle className="size-4 text-gray-500" />,
};

// Status colors
const statusColors = {
  pending: "bg-yellow-50 text-yellow-700 border-yellow-200",
  paid: "bg-green-50 text-green-700 border-green-200",
  overdue: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-gray-50 text-gray-700 border-gray-200",
};

// Border colors based on status
const borderColors = {
  pending: "border-l-yellow-500",
  paid: "border-l-green-500",
  overdue: "border-l-red-500",
  cancelled: "border-l-gray-500",
};

const InvoiceCard = ({ invoice }) => {
  const auth = useAuth();
  const { t } = useTranslation();

  // Calculate financial totals
  const totalCost = invoice.totalCost || 0;
  const totalAmount = invoice.totalAmount || 0;
  const totalNet = invoice.totalNet || 0;

  return (
    <Card
      className={`w-full shadow-md transition-all duration-300 hover:shadow-lg hover:scale-[1.02] border-l-4 ${
        borderColors[invoice.status] || "border-l-blue-500"
      }`}
    >
      <div className="p-4 space-y-4">
        {/* Header Section with Actions */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Receipt className="size-4 text-blue-600" />
            <Link to={`/invoices/${invoice._id}`}>
              <h3 className="text-sm font-semibold text-gray-900 hover:text-blue-600 transition-colors">
                #{invoice._id.substring(0, 8)}...
              </h3>
            </Link>
          </div>

          <div className="flex items-center gap-2">
            {/* Actions - includes both PDF actions and admin actions */}
            <InvoiceActions
              invoiceId={invoice._id}
              invoice={invoice}
              clientInfo={invoice.client}
              showPDFActions={true}
              showMarkAsPaid={auth.isAdmin && invoice.status === "pending"}
            />
          </div>
        </div>

        {/* Status and Package Count */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Badge
              variant="outline"
              className={`${statusColors[invoice.status]} font-medium`}
            >
              {statusIcons[invoice.status]}
              <span className="ml-1">{t(`status:${invoice.status}`)}</span>
            </Badge>

            <div className="flex items-center gap-1 text-sm text-primary-foreground">
              <Package className="size-4" />
              <span className="font-medium">
                {invoice.packages?.length || 0}
              </span>
              <span>{t("packages.packages")}</span>
            </div>
          </div>
        </div>

        {/* Client Information */}
        {auth.isAdmin && invoice.client && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Store className="size-4" />
                <span className="font-medium">{invoice.client.brandName}</span>
              </div>
              {invoice.client.phone && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="size-4" />
                  <span className="font-medium">{invoice.client.phone}</span>
                </div>
              )}
            </div>
            {invoice.client.city && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <MapPin className="size-4" />
                <span>{invoice.client.city}</span>
              </div>
            )}
          </div>
        )}

        {/* Financial Summary */}
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <DollarSign className="size-4" />
              {t("invoices.financialSummary")}
            </h4>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className="size-4 text-gray-400" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">{t("invoices.financialTooltip")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>

          <div className="grid grid-cols-3 gap-2 text-xs">
            <div className="text-center p-2 bg-white rounded border">
              <div className="font-bold text-gray-700">
                {totalCost.toFixed(2)}
              </div>
              <div className="text-gray-500">{t("common.cost")}</div>
            </div>
            <div className="text-center p-2 bg-blue-50 rounded border border-blue-200">
              <div className="font-bold text-blue-700">
                {totalAmount.toFixed(2)}
              </div>
              <div className="text-blue-600">{t("common.amount")}</div>
            </div>
            <div className="text-center p-2 bg-green-50 rounded border border-green-200">
              <div className="font-bold text-green-700">
                {totalNet.toFixed(2)}
              </div>
              <div className="text-green-600">{t("invoices.totalNet")}</div>
            </div>
          </div>

          <div className="mt-2 text-center">
            <span className="text-xs text-gray-500">
              {t("common.currency")}
            </span>
          </div>
        </div>

        <Separator />

        {/* Footer Information */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-1">
            <CalendarIcon className="size-3" />
            <span>
              {t("common.created")}:{" "}
              {format(new Date(invoice.createdAt), "dd/MM/yyyy HH:mm")}
            </span>
          </div>

          <Link to={`/invoices/${invoice._id}`}>
            <Button
              variant="ghost"
              size="sm"
              className="h-auto p-1 text-xs hover:text-blue-600"
            >
              <Eye className="size-3 mr-1" />
              {t("common.viewDetails")}
            </Button>
          </Link>
        </div>

        {/* Additional info for client's own invoice */}
        {!auth.isAdmin && invoice.client && (
          <div className="flex items-center gap-2 text-xs text-muted-foreground bg-blue-50 p-2 rounded">
            <User2 className="size-3" />
            <span>{invoice.client.brandName}</span>
          </div>
        )}
      </div>
    </Card>
  );
};

export default InvoiceCard;
